/**
 * NuevoMantenimientoDialog — Crear un mantenimiento (puede heredar de plantilla).
 */
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Stack, TextField, MenuItem, Switch, FormControlLabel, Alert,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import SlideOver from '../common/SlideOver';
import useToast from '../common/useToast';
import { usePlantillas, useEmbarcaciones } from './shared/useFlota';
import {
  API_BASE, getAuthHeaders, getEmpresaId, CATEGORIAS, TIPOS_EVENTO,
} from './shared/format';

export default function NuevoMantenimientoDialog({ open, onClose, onCreated, embarcacionId = null }) {
  const { toast, ToastContainer } = useToast();
  const { plantillas } = usePlantillas();
  const { embarcaciones } = useEmbarcaciones();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    embarcacion_id: embarcacionId || '',
    plantilla_id: '',
    nombre: '',
    categoria: 'motor',
    tipo_evento: 'preventivo',
    bloquea_operacion: false,
    fecha_programada: '',
    proveedor: '',
    costo: '',
    observaciones: '',
  });

  // Cuando seleccionan plantilla, heredar valores
  useEffect(() => {
    if (!form.plantilla_id) return;
    const pl = plantillas.find(p => p.id === parseInt(form.plantilla_id));
    if (pl) {
      setForm(f => ({
        ...f,
        nombre: pl.nombre,
        categoria: pl.categoria,
        bloquea_operacion: pl.obligatorio_para_zarpe || f.bloquea_operacion,
      }));
    }
  }, [form.plantilla_id, plantillas]);

  const guardar = async () => {
    if (!form.embarcacion_id) { toast.error('Selecciona una embarcación'); return; }
    if (!form.nombre.trim()) { toast.error('Nombre requerido'); return; }
    if (!form.fecha_programada) { toast.error('Fecha requerida'); return; }
    setSaving(true);
    try {
      const body = {
        empresa_id: getEmpresaId(),
        embarcacion_id: parseInt(form.embarcacion_id),
        plantilla_id: form.plantilla_id ? parseInt(form.plantilla_id) : null,
        nombre: form.nombre.trim(),
        categoria: form.categoria,
        tipo_evento: form.tipo_evento,
        bloquea_operacion: form.bloquea_operacion,
        fecha_programada: form.fecha_programada,
        proveedor: form.proveedor || null,
        costo: form.costo ? Number(form.costo) : null,
        observaciones: form.observaciones || null,
      };
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        body.registrado_por_user_id = u.id || u.user_id || null;
      } catch {}

      const r = await fetch(`${API_BASE}/turismo/mantenimiento/mantenimientos`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json(); toast.error(e.detail || 'Error'); setSaving(false); return; }
      toast.success('Mantenimiento creado');
      onCreated?.();
    } catch (e) { toast.error('Error de red'); }
    setSaving(false);
  };

  return (
    <SlideOver open={open} onClose={onClose}
      title="Nuevo mantenimiento" icon={<BuildIcon />}
      footer={
        <>
          <Button onClick={onClose} sx={{ textTransform: 'none' }} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving} disableElevation
            sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
            {saving ? 'Creando…' : 'Crear'}
          </Button>
        </>
      }>
      <Stack spacing={1.5}>
        <TextField size="small" select label="Embarcación *" value={form.embarcacion_id}
          onChange={e => setForm({ ...form, embarcacion_id: e.target.value })}>
          {embarcaciones.map(e => <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>)}
        </TextField>

        <TextField size="small" select label="Plantilla (opcional)" value={form.plantilla_id}
          onChange={e => setForm({ ...form, plantilla_id: e.target.value })}
          helperText="Si seleccionas plantilla, hereda nombre, categoría y bloqueo.">
          <MenuItem value="">— Sin plantilla (ad hoc) —</MenuItem>
          {plantillas.filter(p => p.activo !== false).map(p => (
            <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
          ))}
        </TextField>

        <TextField size="small" label="Nombre de la tarea *" value={form.nombre}
          onChange={e => setForm({ ...form, nombre: e.target.value })} />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <TextField size="small" select label="Categoría" value={form.categoria}
            onChange={e => setForm({ ...form, categoria: e.target.value })}>
            {CATEGORIAS.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Tipo" value={form.tipo_evento}
            onChange={e => setForm({ ...form, tipo_evento: e.target.value })}>
            {TIPOS_EVENTO.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </Box>

        <TextField size="small" type="date" label="Fecha programada *" value={form.fecha_programada}
          InputLabelProps={{ shrink: true }}
          onChange={e => setForm({ ...form, fecha_programada: e.target.value })} />

        <TextField size="small" label="Proveedor (opcional)" value={form.proveedor}
          onChange={e => setForm({ ...form, proveedor: e.target.value })} />

        <TextField size="small" type="number" label="Costo estimado (opcional)" value={form.costo}
          onChange={e => setForm({ ...form, costo: e.target.value })} />

        <TextField size="small" multiline minRows={2} label="Observaciones" value={form.observaciones}
          onChange={e => setForm({ ...form, observaciones: e.target.value })} />

        <FormControlLabel control={<Switch checked={form.bloquea_operacion}
          onChange={e => setForm({ ...form, bloquea_operacion: e.target.checked })} />}
          label="Bloquea operación (alerta en embarcación si vence)" />

        {form.bloquea_operacion && (
          <Alert severity="warning" sx={{ fontSize: 12 }}>
            Si esta tarea se vence, la embarcación mostrará una alerta visual roja.
          </Alert>
        )}
      </Stack>
      <ToastContainer />
    </SlideOver>
  );
}
